import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { CELL_WIDTH } from '../../../../core/constants';
import { calculateDataGridLayout, DataGridLayout } from '../../../../ui/data-grid/components/data-grid-layout';
import { getLinesFirstInvalidCellIndex } from '../../utils';
import useDraggableScroll from '../../../../hooks/useDraggableScroll';

const DATE_PART_LENGTH = 2;
const SCROLL_COLUMN_COUNT = 6;
const SCROLL_SHIFT = SCROLL_COLUMN_COUNT * CELL_WIDTH;

export enum DIRECTION {
  'back',
  'forward'
}

export interface AccountsImpactedLine {
  name: string;
  data: string[];
  valid?: boolean[];
}

interface Props {
  columns: string[];
  impactedLines?: AccountsImpactedLine[];
}

interface UseAccountsImpactedGridResult {
  invalidLines: Record<string, boolean>;
  layout: DataGridLayout;
  onMouseDown: (event: { clientX: number; clientY: number }) => void;
  ref: RefObject<HTMLDivElement>;
  slide: (direction: DIRECTION) => void;
}

/**
 * Manages the Accounts Impacted grid layout, viewport measurement, and horizontal scrolling.
 *
 * The grid has its own viewport because it is rendered outside the page-level data-grid provider.
 */
export const useAccountsImpactedGrid = ({ columns, impactedLines }: Props): UseAccountsImpactedGridResult => {
  const [invalidLines, setInvalidLines] = useState<Record<string, boolean>>({});
  const [viewportWidth, setViewportWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const { onMouseDown } = useDraggableScroll(ref, { direction: 'horizontal' });
  const layout = calculateDataGridLayout(columns.length, viewportWidth);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    /** Measures the space remaining for date columns after the account-name area has taken its width. */
    const measureViewport = () => setViewportWidth(element.clientWidth);

    measureViewport();

    if (typeof ResizeObserver !== 'undefined') {
      const observerCallback: ResizeObserverCallback = (_entries, _observer) => {
        measureViewport();
      };
      const observer = new ResizeObserver(observerCallback);
      observer.observe(element);

      return () => observer.disconnect();
    }

    window.addEventListener('resize', measureViewport);
    return () => window.removeEventListener('resize', measureViewport);
  }, [columns.length, impactedLines?.length]);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(DATE_PART_LENGTH, '0')}`;
    const dateIndex = columns.findIndex(column => column === dateStr);

    element.scrollTo({ left: dateIndex * layout.columnWidth });
  }, [columns, impactedLines, layout.columnWidth]);

  useEffect(() => {
    const [newInvalidLines, firstInvalidLine] = getLinesFirstInvalidCellIndex(impactedLines || []);
    setInvalidLines(newInvalidLines);

    const element = ref.current;
    if (element && firstInvalidLine !== -1) {
      element.scrollTo({ left: firstInvalidLine * layout.columnWidth });
    }
  }, [impactedLines, layout.columnWidth]);

  useEffect(() => {
    if (layout.fitsInViewport) {
      ref.current?.scrollTo({ left: 0 });
    }
  }, [layout.fitsInViewport]);

  const slide = useCallback((direction: DIRECTION) => {
    const element = ref.current;
    if (!element) {
      return;
    }

    let start: number | undefined;
    const time = 500;
    const currentPosition = element.scrollLeft ?? 0;
    const endPosition = direction === DIRECTION.back ? currentPosition - SCROLL_SHIFT : currentPosition + SCROLL_SHIFT;

    const step = (currentTime: number) => {
      if (start === undefined) {
        start = currentTime;
      }

      const progress = currentTime - start;

      if (currentPosition < endPosition) {
        element.scrollTo({ left: ((endPosition - currentPosition) * progress) / time + currentPosition });
      } else {
        element.scrollTo({ left: currentPosition - ((currentPosition - endPosition) * progress) / time });
      }

      if (progress < time) {
        window.requestAnimationFrame(step);
      } else {
        element.scrollTo({ left: endPosition });
      }
    };

    window.requestAnimationFrame(step);
  }, []);

  return { invalidLines, layout, onMouseDown, ref, slide };
};
